import { GameConstants } from "@common/constants";
import { OCTO_ACTION } from "@common/definitions/octo";
import { DefinitionType } from "@common/utils/objectDefinitions";
import { FireCellState } from "@common/definitions/fire";
import { Vec } from "@common/utils/vector";
import { Game } from "../game";
import { t } from "../utils/translations/phrases";
import { FireEffects } from "./fireEffects";
import { InputManager } from "./inputManager";
import { UIManager } from "./uiManager";

/** how far a struck match reaches (server: MeleeItem.strikeMatch lights fuel within 4.5 units) */
const MATCH_REACH = 4.5;
const MELEE_SLOT = GameConstants.player.inventorySlotTypings.indexOf(DefinitionType.Melee);

/** a key's name as players know it: "Mouse0" -> "LMB", "KeyR" -> "R" */
const keyLabel = (input: string | undefined): string => {
    if (!input) return "";
    const mouse = /^Mouse(\d)$/.exec(input);
    if (mouse) return [t("LMB"), t("MMB"), t("RMB"), "M4", "M5"][Number(mouse[1])] ?? input;
    return input.replace(/^Key/, "").replace(/^Digit/, "").replace(/^Arrow/, "").toUpperCase();
};

/** the Octo Gadget's hint stays this long after the gadget is picked up (1.10; its fade is the page's, hint-fade) */
const OCTO_HINT_MS = 9000;

/**
 * Bottom-centre hints for actions that are hard to guess: with the Gas Can in hand and poured fuel within
 * reach of a match, a flashing "R · Strike a match"; with C4 planted, "Z · Detonate C4" (whatever the keys
 * are bound to); the Octo Gadget just picked up, "SHIFT · Shoot an octo arm" for a while, in yellow (1.10, the owner:
 * "write that in the bottom in the yellow, small but for some time, just so people know about the button when the
 * gadget is picked"). They only touch the page when something changes.
 */
class ActionHintsClass {
    private _fuel?: HTMLElement;
    private _c4?: HTMLElement;
    private _octo?: HTMLElement;
    private _state = "";
    /** the Octo Gadget: whether the melee slot held it last look, and till when its hint shows */
    private _hadOcto = false;
    private _octoUntil = 0;

    private _fuelNear(): boolean {
        const position = Game.activePlayer?.position;
        if (!position) return false;
        for (let dx = -MATCH_REACH; dx <= MATCH_REACH; dx += 1.5) {
            for (let dy = -MATCH_REACH; dy <= MATCH_REACH; dy += 1.5) {
                if (dx * dx + dy * dy > MATCH_REACH * MATCH_REACH) continue;
                if (FireEffects.stateAt(Vec(position.x + dx, position.y + dy)) === FireCellState.Fuel) return true;
            }
        }
        return false;
    }

    /** checked ten times a second, not every frame */
    private _nextCheck = 0;

    update(): void {
        const now = performance.now();
        if (now < this._nextCheck) return;
        this._nextCheck = now + 100;
        this._fuel ??= document.getElementById("hint-light-fuel") ?? undefined;
        this._c4 ??= document.getElementById("hint-detonate-c4") ?? undefined;
        this._octo ??= document.getElementById("hint-octo") ?? undefined;
        if (!this._fuel || !this._c4 || !this._octo) return;

        const player = Game.activePlayer;
        const inventory = UIManager.inventory;
        const weapon = inventory.weapons[inventory.activeWeaponIndex];
        const holdingCan = !!weapon && weapon.definition.defType === DefinitionType.Melee && "pour" in weapon.definition && !!weapon.definition.pour;
        const showFuel = !!player && !player.dead && Game.gameStarted && holdingCan && this._fuelNear();
        const showC4 = !!player && !player.dead && Game.gameStarted && UIManager.hasC4s;
        const mobile = InputManager.isMobile;
        const lightKey = mobile ? "" : keyLabel(InputManager.binds.getInputsBoundToAction("reload")[0]);
        const boomKey = mobile ? "" : keyLabel(InputManager.binds.getInputsBoundToAction("explode_c4")[0]);
        // the Octo Gadget (1.10): picked up (the melee slot has it, and hadn't), its key shows a while
        const melee = inventory.weapons[MELEE_SLOT];
        const hasOcto = !!melee && melee.definition.defType === DefinitionType.Melee && (melee.definition as { octo?: true }).octo === true;
        if (hasOcto && !this._hadOcto && Game.gameStarted) this._octoUntil = now + OCTO_HINT_MS;
        this._hadOcto = hasOcto;
        // (never a watched player's: the key is yours; nor in the replay of a death)
        const showOcto = hasOcto && !!player && !player.dead && !Game.spectating && !Game.replaying && now < this._octoUntil;
        const octoKey = mobile ? "" : keyLabel(InputManager.binds.getInputsBoundToAction(OCTO_ACTION)[0]);

        const state = `${showFuel}|${showC4}|${lightKey}|${boomKey}|${mobile}|${showOcto}|${octoKey}`;
        if (state === this._state) return;
        this._state = state;
        this._fuel.hidden = !showFuel;
        this._c4.hidden = !showC4;
        this._octo.hidden = !showOcto;
        this._fuel.querySelector(".th-keycap")!.textContent = lightKey;
        this._c4.querySelector(".th-keycap")!.textContent = boomKey;
        this._fuel.querySelector(".th-hint-text")!.textContent = mobile ? t("Tap reload to light") : t("Strike a match");
        const octoKeycap = this._octo.querySelector(".th-keycap");
        if (octoKeycap) octoKeycap.textContent = octoKey;
        const octoText = this._octo.querySelector(".th-hint-text");
        if (octoText) octoText.textContent = mobile || octoKey === "" ? t("Attack with the gadget in hand to shoot an arm") : t("Shoot an octo arm");
    }

    reset(): void {
        this._state = "";
        this._nextCheck = 0;
        this._hadOcto = false;
        this._octoUntil = 0;
        if (this._fuel) this._fuel.hidden = true;
        if (this._c4) this._c4.hidden = true;
        if (this._octo) this._octo.hidden = true;
    }
}

export const ActionHints = new ActionHintsClass();
